import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import PageLayout from "../components/PageLayout";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import Badge from "../components/Badge";
import { GRADE_FILTER_OPTIONS, GRADE_ORDER } from "../components/Grade";
import { Student } from "../types/Student";
import TableInput from "../components/TableInput";
import Dropdown from "../components/Dropdown";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  clickableCellStyle,
  emptyRowStyle,
} from "../components/Style";

const columnHelper = createColumnHelper<Student>();

function StudentManagementPage() {
  // State: Students
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  // State: delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetStudent, setDeleteTargetStudent] = useState<Student | null>(null);
  // State: Sort
  const [sorting, setSorting] = useState<SortingState>([]);
  // State: Filters
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  
  const navigate = useNavigate();

  // API에서 학생 목록 가져오기
  const fetchStudents = async () => {
    try {
      const data = await apiClient.getStudents();
      setStudents(data);
    } catch (error) {
      console.error("학생 목록 조회 실패:", error);
    }
  };

  // API에서 반 목록 가져오기
  const fetchClasses = async () => {
    try {
      const data = await apiClient.getClasses();
      setClasses(data);
    } catch (error) {
      console.error("반 목록 조회 실패:", error);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, []);

  // 반 목록 추출 - DB의 클래스 테이블에서 가져옴
  const classList = useMemo(() => {
    return classes.map(c => c.className).sort();
  }, [classes]);

  // 반 배지 색상 결정
  const getClassBadgeProps = (classCategory: string) => {
    if (classCategory === 'regular') {
      // 정규반: 파란색
      return {
        backgroundColor: 'rgba(227, 242, 253, 0.8)',
        textColor: '#1976d2',
        borderColor: 'rgba(33, 150, 243, 0.35)',
      };
    } else if (classCategory === 'special') {
      // 특강반: 초록색
      return {
        backgroundColor: 'rgba(76, 175, 80, 0.15)',
        textColor: '#2e7d32',
        borderColor: 'rgba(76, 175, 80, 0.35)',
      };
    }
    // 기본값
    return {
      backgroundColor: '#f3f4f6',
      textColor: '#6b7280',
      borderColor: '#d1d5db',
    };
  };

  // 학생 삭제
  const handleDeleteStudent = async (id: number | string) => {
    try {
      await apiClient.deleteStudent(id);
      fetchStudents();
    } catch (error) {
      console.error("학생 삭제 실패:", error);
    }
  };

  // 삭제 확인 핸들러
  const handleConfirmDelete = async () => {
    if (!deleteTargetStudent) return;
    await handleDeleteStudent(deleteTargetStudent.id!);
    setDeleteDialogOpen(false);
    setDeleteTargetStudent(null);
  };

  // 테이블 컬럼 정의
  const columns = useMemo(() => [
    columnHelper.accessor("studentNumber", {
      header: "출결번호",
      size: 100,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/student/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("name", {
      header: "이름",
      size: 100,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/student/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("grade", {
      header: "학년",
      size: 100,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/student/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const gradeA = GRADE_ORDER[rowA.getValue(columnId) as string] || 999;
        const gradeB = GRADE_ORDER[rowB.getValue(columnId) as string] || 999;
        if (gradeA !== gradeB) {
          return gradeA - gradeB;
        }
        return rowA.original.name.localeCompare(rowB.original.name);
      },
    }),
    columnHelper.accessor("classes", {
      header: "반",
      size: 250,
      cell: (info) => {
        const studentClasses = info.getValue() || [];
        if (studentClasses.length === 0) {
          return <span style={{ color: "#999", fontSize: 13 }}>없음</span>;
        }

        // 정규반 먼저, 특강반 나중에 정렬
        const sortedClasses = [...studentClasses].sort((a: any, b: any) => {
          const categoryOrder = { regular: 0, special: 1 };
          const aOrder = categoryOrder[a.classCategory as keyof typeof categoryOrder] ?? 2;
          const bOrder = categoryOrder[b.classCategory as keyof typeof categoryOrder] ?? 2;
          return aOrder - bOrder;
        });

        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={() => navigate(`/student/${info.row.original.id}`)}
          >
            {sortedClasses.map((cls: any) => {
              const badgeProps = getClassBadgeProps(cls.classCategory);
              return (
                <Badge key={cls.id} {...badgeProps}>
                  {cls.className}
                </Badge>
              );
            })}
          </div>
        );
      },
      sortingFn: (rowA, rowB) => {
        const classesA = rowA.original.classes || [];
        const classesB = rowB.original.classes || [];
        // 첫 번째 정규반 이름으로 정렬
        const firstClassA = classesA.find((c: any) => c.classCategory === 'regular')?.className || '';
        const firstClassB = classesB.find((c: any) => c.classCategory === 'regular')?.className || '';
        return firstClassA.localeCompare(firstClassB);
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const studentClasses = row.getValue(columnId) as any[];
        return studentClasses.some((c: any) => c.className === filterValue);
      },
    }),
    columnHelper.accessor("phone", {
      header: "전화번호",
      size: 130,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/student/${info.row.original.id}`)}
        >
          {info.getValue() || ""}
        </span>
      ),
    }),
    columnHelper.accessor("messageRecipient", {
      header: "메시지 수신자",
      size: 80,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/student/${info.row.original.id}`)}
        >
          {info.getValue() || ""}
        </span>
      ),
    }),
    columnHelper.accessor("messagePhone", {
      header: "메시지 수신번호",
      size: 130,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/student/${info.row.original.id}`)}
        >
          {info.getValue() || ""}
        </span>
      ),
    }),
  ], [navigate]);

  // Table instance
  const table = useReactTable({
    data: students,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout
      title="학생 관리"
      actionRows={[
        (
          <button
            key="add-student"
            onClick={() => navigate("/student/new")}
            style={{ ...buttonStyle, ...buttonBackgroundColor.save }}
          >
            + 학생 추가
          </button>
        ),
      ]}
    >
      <style>
        {`
          .student-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="student-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                            {columnId === "studentNumber" && (
                              <TableInput
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder="검색"
                              />
                            )}
                            {columnId === "name" && (
                              <TableInput
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder="검색"
                              />
                            )}
                            {columnId === "grade" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...GRADE_FILTER_OPTIONS,
                                ]}
                              />
                            )}
                            {columnId === "class" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...classList.map((c) => ({ value: c, label: c })),
                                ]}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  학생이 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteTargetStudent(null);
        }}
        onConfirm={handleConfirmDelete}
        itemName={deleteTargetStudent?.name || ""}
        itemType="학생"
      />
    </PageLayout>
  );
}

export default StudentManagementPage;